import { useEffect, useState } from "react"
import { Column, DetailPage, Row } from "../../components/crud/DetailPage"
import { ListPage } from "../../components/crud/ListPage"
import { AsyncSelect, Checkbox, DateInput, ImageInput, MoneyInput, NumberInput, Select, TextareaInput, TextInput, TimeInput } from "../../components/forms/Inputs"
import { useOptions } from "../../hooks/useOptions"
import { useLocale, useTranslations } from "../../layers/Localization"
import { CodesList } from "./Codes"
import { HistoriquesList } from "./Historique"
import { PrixChantiersList } from "./PrixChantiers"
import { TempsList } from "./Temps"
import { Popup } from "../../components/layout/Popup"
import { Form } from "../../components/forms/Form"
import { useObjectState } from "../../hooks/useObjectState"
import { useApi } from "../../hooks/useApi"
import { FaBan, FaPercent, FaSave } from "react-icons/fa"
import { Button } from "../../components/layout/Button"
import { ContactsList } from "./Contacts"
import { PlaintesList } from "./Plaintes"
import { FacturesList } from "./Factures"
import { Currency, postalCodeToCommune, toCurrency } from "../../hooks/helpers"
import { MaterielList } from "./Materiel"
import { useToday } from "../../hooks/useToday"
import { useSociete } from "../../hooks/useSociete"
import { useParams } from "react-router-dom"
import { useRegimeTvaOptions } from "./Clients"
import { useCodesTvaOptions } from "./Articles"

export const useTypeOptions = () => {
    const t = useTranslations()
    return [
        { value: 'IMMEUBLE', label: t('Immeuble'), minNum: "00001", maxNum: "09999", description: `NETTOYAGE DES COMMUNS DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'BUREAU', label: t('Bureau'), minNum: "10000", maxNum: "19999", description: `NETTOYAGE DE VOTRE BUREAU SITUE {adresse}, {codePostal} {commune}` },
        { value: 'REMPLACEMENT_DE_CONCIERGE', label: t('Remplacement de concierge'), minNum: "20000", maxNum: "29999", description: `REMPLACEMENT DU CONCIERGE DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'PARKING', label: t('Parking'), minNum: "20000", maxNum: "29999", description: `NETTOYAGE DU PARKING DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'VITRES', label: t('Vitres'), minNum: "20000", maxNum: "29999", description: `NETTOYAGE DES VITRES DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'JARDIN', label: t('Jardin'), minNum: "20000", maxNum: "29999", description: `NETTOYAGE DU JARDIN DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'PRODUITS_OU_MATERIEL', label: t('Produits ou materiel'), minNum: "20000", maxNum: "29999", description: `PRODUITS ET MATERIELS POUR VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'TAPIS', label: t('Tapis'), minNum: "20000", maxNum: "29999", description: `NETTOYAGE DU TAPIS DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'OCCASIONNEL', label: t('Occasionnel'), minNum: "20000", maxNum: "29999", description: `NETTOYAGE DE VOTRE IMMEUBLE SITUE {adresse}, {codePostal} {commune}` },
        { value: 'VENTE', label: t('Vente'), minNum: "20000", maxNum: "29999", description: `` },
        { value: 'AUTRES', label: t('Autres'), minNum: "20000", maxNum: "29999", description: `` },
    ]
}

const useFrequenceOptions = () => {
    const t = useTranslations()
    return [
        { value: 'HEBDOMADAIRE', label: t('Hebdomadaire') },
        { value: 'FREQUENTIEL', label: t('Fréquentiel') },
        { value: 'A_LA_DEMANDE', label: t('A la demande') },
    ]
}

const useActions = (client, offre) => {
    const t = useTranslations()
    const api = useApi()
    const societe = useSociete()
    return {
        [t("Dupliquer")]: (item, reload) => api.post(`/chantiers/${item.id}/duplicate`).then(reload),
        [t("Ouvrir contrat")]: { href: item => societe?.contractFolder ? 'file:///' + societe.contractFolder.replaceAll('{nom}', (item.client?.referenceCourte || client?.referenceCourte || '').replaceAll(' ', '_')).replaceAll('{num}', item.offre?.num || offre?.num) : null },
        [t("Ouvrir check-in")]: { href: item => societe?.checkInFolder ? 'file:///' + societe.checkInFolder.replaceAll('{nom}', (item.client?.referenceCourte || client?.referenceCourte || '').replaceAll(' ', '_')) : null },
        [t("Ouvrir programme de travail")]: { href: item => societe?.programmeFolder ? `ms-word:ofe|u|file:///` + societe.programmeFolder.replaceAll('{nom}', (item?.nom || '').replaceAll(' ', '_')) : null },
    }
}

export function ChantierFields({ item, set, originalItem, tab }) {
    const [client, setClient] = useState()
    const [temps, setTemps] = useState([])
    const t = useTranslations()
    const [locale] = useLocale()
    const clientOptions = useOptions('clients', ['num', 'referenceCourte'])
    const offreOptions = useOptions('offres', 'num')
    const codeTvaOptions = useCodesTvaOptions()
    const poubOptions = useOptions('codesSortiePoubelles', 'code', { query: '', link: false })
    const frequenceExecutionOptions = useOptions('frequences', [locale === 'fr' ? 'textFr' : locale === 'nl' ? 'textNl' : 'textEn'])
    const verreFrequenceOptions = useOptions('verreFrequence', 'nom')
    const regimeTvaOptions = useRegimeTvaOptions()
    const typeOptions = useTypeOptions()
    const frequenceOptions = useFrequenceOptions()
    const today = useToday()
    const api = useApi()
    const { id } = useParams()

    const totalTimeHebdomadaire = (item.tempsLundi || 0) + (item.tempsMardi || 0) + (item.tempsMercredi || 0) + (item.tempsJeudi || 0) + (item.tempsVendredi || 0) + (item.tempsSamedi || 0) + (item.tempsDimanche || 0)
    const totalTimeFrequentiel = (item.tempsJanvier || 0) + (item.tempsFevrier || 0) + (item.tempsMars || 0) + (item.tempsAvril || 0) + (item.tempsMai || 0) + (item.tempsJuin || 0) + (item.tempsJuillet || 0) + (item.tempsAout || 0) + (item.tempsSeptembre || 0) + (item.tempsOctobre || 0) + (item.tempsNovembre || 0) + (item.tempsDecembre || 0)
    const totalTime = item.frequence === 'HEBDOMADAIRE' ? totalTimeHebdomadaire : item.frequence === 'FREQUENTIEL' ? totalTimeFrequentiel : item.frequence === 'A_LA_DEMANDE' ? item.tempsALaDemande : null
    const hourlyRate = !item.frequence || !item.prixVente ? 0 : item.frequence === 'HEBDOMADAIRE' ? item.prixVente / totalTimeHebdomadaire / 4.33 : item.frequence === 'FREQUENTIEL' ? item.prixVente / Math.max((item.tempsJanvier || 0), (item.tempsFevrier || 0), (item.tempsMars || 0), (item.tempsAvril || 0), (item.tempsMai || 0), (item.tempsJuin || 0), (item.tempsJuillet || 0), (item.tempsAout || 0), (item.tempsSeptembre || 0), (item.tempsOctobre || 0), (item.tempsNovembre || 0), (item.tempsDecembre || 0)) : item.frequence === 'A_LA_DEMANDE' ? item.prixVente / item.tempsALaDemande : 0
    const openTemps = temps.filter(x => !x.dateFin)
    const invalidLastDate = item.dateFin && openTemps.length && t("Impossible de fermer le chantier car il reste des temps non cloturés: {temps}", { temps: openTemps.map(x => x.personnel?.prenom).join(', ') })
    const marginAmount = item.prixVente - item.prixAchat
    const marginPercentage = Math.round(marginAmount / item.prixVente * 100 * 10) / 10

    useEffect(() => {
        if (item.id) api.get(`/temps?chantierId=${encodeURIComponent(item.id)}&dateFin=&personnel:join=`).then(setTemps)
    }, [item.id])

    useEffect(() => {
        if (id === 'new' && client) {
            set('num')(client.num)
            set('nom')(client.referenceCourte)
            set('adresse')(client.adresse)
            set('codePostal')(client.cp)
            set('commune')(client.commune)
            set('dateDebut')(client.datePremierTravail)
            set('type')(client.type)
            set('descriptionFacture')(t(typeOptions.find(x => x.value === client.type).description, { adresse: client.adresse, codePostal: client.cp, commune: client.commune }, client.langue.toLowerCase()).toUpperCase())
            set('codeTva')(regimeTvaOptions.find(x => x.value === client.regimeTva).codeTva)
        }
    }, [id, client])

    const onCodePostalChange = value => {
        set('codePostal')(value)
        set('commune')(postalCodeToCommune(value))
    }
    const onChangePrixAchat = value => {
        set('prixAchat')(value)
        set('majPrixAchat')(today)
    }
    const onChangePrixVente = value => {
        set('prixVente')(value)
        set('majPrixVente')(today)
    }
    const onOffreChange = (value, item) => {
        set('offreId')(value)
        set('photo')(item.photo)
        if (!item.prix2) set('prixVente')(item.prix1)
    }
    const onDescriptionFactureChange = value => {
        set('descriptionFacture')(value.toUpperCase())
    }


    return <>
        {tab === t('Administration') && <>
            <Row start>
                <ImageInput label={t("Photo")} value={item.photo} onChange={set('photo')} />
                <Column>
                    <Row>
                        <TextInput label={t("Numéro")} value={item.num} onChange={set('num')} required />
                        <TextInput className="grow" label={t("Nom")} value={item.nom} onChange={set('nom')} required />
                    </Row>
                    <Row>
                        <Select label={t("Type")} value={item.type} onChange={set('type')} options={typeOptions} />
                        <TextInput className="grow" label={t("Nom de l'immeuble")} value={item.nomImmeuble} onChange={set('nomImmeuble')} />
                    </Row>
                </Column>
            </Row>
            <Row>
                <AsyncSelect label={t("Offre/contrat")} value={item.offreId} onChange={onOffreChange} options={offreOptions} />
                <AsyncSelect className="grow" label={t("Client")} value={item.clientId} onChange={set('clientId')} onItemLoad={setClient} options={clientOptions} required />
            </Row>
            <Row>
                <TextInput className="grow" label={t("Adresse du chantier")} value={item.adresse} onChange={set('adresse')} required />
                <TextInput label={t("Code postal")} value={item.codePostal} onChange={onCodePostalChange} required />
                <TextInput label={t("Commune")} value={item.commune} onChange={set('commune')} required />
            </Row>
            <hr />
            <Row>
                <DateInput label={t("Date de début")} value={item.dateDebut} onChange={set('dateDebut')} required />
                <DateInput label={t("Date de fin")} value={item.dateFin} onChange={set('dateFin')} invalid={invalidLastDate} />
                <TextInput className="grow" label={t("Raison de fin")} value={item.raisonDeFin} onChange={set('raisonDeFin')} />
            </Row>
            <Row>
                <TextInput label={t("Déclaration de travaux")} value={item.numDeclarationTravaux} onChange={set('numDeclarationTravaux')} pattern="^[0-9A-Z]{3}-[0-9A-Z]{7}-[0-9A-Z]{2}-[0-9A-Z]{1}$" />
            </Row>
            <hr />
            <Checkbox label={t("Déneigement")} value={item.deneigement} onChange={set('deneigement')} />
            <Row>
                <NumberInput label={t('Numéro de client BXL propreté')} value={item.bxlPropreteNumClient} onChange={set('bxlPropreteNumClient')} />
                <NumberInput label={t('Numéro de contrat BXL propreté')} value={item.bxlPropreteNumContrat} onChange={set('bxlPropreteNumContrat')} />
                <div className="font-bold mt-10">{item.bxlPropreteNumContrat || item.bxlPropreteNumClient ? `Client N° ${item.bxlPropreteNumClient || ''} - Contrat N° ${item.bxlPropreteNumContrat || ''}` : ''}</div>
            </Row>
            {item.dateFin && originalItem && !originalItem.dateFin && <div className="text-red-600 font-bold">{t("N'oubliez pas de modifier la déclaration de travaux et le checking !")}</div>}
            <hr />
            <TextareaInput label={t("Mémo travailleurs")} value={item.memoTravailleurs} onChange={set('memoTravailleurs')} />
            <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={item.memoInterne} onChange={set('memoInterne')} />
        </>}
        {tab === t('Facturation') && <>
            <Row>
                <Select label={t("Régime TVA")} value={item.codeTva} onChange={set('codeTva')} options={codeTvaOptions} required />
                <TextInput className="grow" label={t("Description de la facture")} value={item.descriptionFacture} onChange={onDescriptionFactureChange} required />
            </Row>
            <Row>
                <Checkbox label={t("Facturation mensuelle")} value={item.facturationMensuel} onChange={set('facturationMensuel')} />
                <Checkbox label={t("A vérifier")} value={item.aVerifier} onChange={set('aVerifier')} />
            </Row>
            <hr />
            <Row>
                <Column>
                    <Row>
                        <MoneyInput className="grow" label={t("Prix d'achat")} value={item.prixAchat} onChange={onChangePrixAchat} required max={99999} />
                        <DateInput label={t("Mise à jour du prix d'achat")} value={item.majPrixAchat} onChange={set('majPrixAchat')} />
                    </Row>
                    <Checkbox label={t("Indexer prix d'achat")} value={item.indexerPrixAchat} onChange={set('indexerPrixAchat')} />
                </Column>
                <Column>
                    <Row>
                        <MoneyInput className="grow" label={t("Prix de vente")} value={item.prixVente} onChange={onChangePrixVente} required max={99999} />
                        <DateInput label={t("Mise à jour du prix de vente")} value={item.majPrixVente} onChange={set('majPrixVente')} />
                    </Row>
                    <Row>
                        <Checkbox label={t("Indexer prix de vente")} value={item.indexerPrixVente} onChange={set('indexerPrixVente')} />
                        {(item.prixAchat != null && item.prixVente != null) && <div className="ml-auto font-bold">{t('Marge: {amount} - {percentage}%', { amount: toCurrency(marginAmount), percentage: marginPercentage })}</div>}
                    </Row>
                </Column>
            </Row>
        </>}
        {tab === t('Heures') && <>
            <Row>
                <Select label={t("Fréquence")} value={item.frequence} onChange={set('frequence')} options={frequenceOptions} required />
            </Row>
            {(item.frequence === 'HEBDOMADAIRE') && <>
                <div className="grid grid-cols-[repeat(8,minmax(100px,1fr))] gap-2 p-1 overflow-auto md:overflow-visible">
                    <div></div>
                    <div className="flex justify-center items-center">{t('Lundi')}</div>
                    <div className="flex justify-center items-center">{t('Mardi')}</div>
                    <div className="flex justify-center items-center">{t('Mercredi')}</div>
                    <div className="flex justify-center items-center">{t('Jeudi')}</div>
                    <div className="flex justify-center items-center">{t('Vendredi')}</div>
                    <div className="flex justify-center items-center">{t('Samedi')}</div>
                    <div className="flex justify-center items-center">{t('Dimanche')}</div>
                    <div className="flex justify-end items-center text-end pr-5">{t('Durée:')}</div>
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsLundi} onChange={set('tempsLundi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsMardi} onChange={set('tempsMardi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsMercredi} onChange={set('tempsMercredi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsJeudi} onChange={set('tempsJeudi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsVendredi} onChange={set('tempsVendredi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsSamedi} onChange={set('tempsSamedi')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsDimanche} onChange={set('tempsDimanche')} step={0.001} shownDecimals={2} required />
                    <div className="flex justify-end items-center text-end pr-5">{t('Heures:')}</div>
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireLundi} onChange={set('heureObligatoireLundi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireMardi} onChange={set('heureObligatoireMardi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireMercredi} onChange={set('heureObligatoireMercredi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireJeudi} onChange={set('heureObligatoireJeudi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireVendredi} onChange={set('heureObligatoireVendredi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireSamedi} onChange={set('heureObligatoireSamedi')} />
                    <TimeInput className="w-[100px] min-w-full" value={item.heureObligatoireDimanche} onChange={set('heureObligatoireDimanche')} />
                    <div className="flex justify-end items-center text-end pr-5">{t('Poubelles:')}</div>
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubLundiId} onChange={set('poubLundiId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubMardiId} onChange={set('poubMardiId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubMercrediId} onChange={set('poubMercrediId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubJeudiId} onChange={set('poubJeudiId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubVendrediId} onChange={set('poubVendrediId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubSamediId} onChange={set('poubSamediId')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubDimancheId} onChange={set('poubDimancheId')} options={poubOptions} />
                    <div className="flex justify-end items-center text-end pr-5"></div>
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubLundi2Id} onChange={set('poubLundi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubMardi2Id} onChange={set('poubMardi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubMercredi2Id} onChange={set('poubMercredi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubJeudi2Id} onChange={set('poubJeudi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubVendredi2Id} onChange={set('poubVendredi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubSamedi2Id} onChange={set('poubSamedi2Id')} options={poubOptions} />
                    <AsyncSelect className="w-[100px] min-w-full" value={item.poubDimanche2Id} onChange={set('poubDimanche2Id')} options={poubOptions} />
                </div>
                <Row>
                    <AsyncSelect label={t('Fréquence')} value={item.frequenceExecutionId} onChange={set('frequenceExecutionId')} options={frequenceExecutionOptions} />
                    <AsyncSelect label={t('Fréquence verre (BXL propreté)')} value={item.frequenceVerreId} onChange={set('frequenceVerreId')} options={verreFrequenceOptions} />
                </Row>
            </>}
            {(item.frequence === 'FREQUENTIEL') && <>
                <div className="grid grid-cols-[repeat(7,minmax(100px,1fr))] gap-2 p-1 overflow-auto md:overflow-visible">
                    <div></div>
                    <div className="flex justify-center items-center">{t('Janvier')}</div>
                    <div className="flex justify-center items-center">{t('Février')}</div>
                    <div className="flex justify-center items-center">{t('Mars')}</div>
                    <div className="flex justify-center items-center">{t('Avril')}</div>
                    <div className="flex justify-center items-center">{t('Mai')}</div>
                    <div className="flex justify-center items-center">{t('Juin')}</div>
                    <div className="flex justify-end items-center text-end pr-5">{t('Durée:')}</div>
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsJanvier} onChange={set('tempsJanvier')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsFevrier} onChange={set('tempsFevrier')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsMars} onChange={set('tempsMars')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsAvril} onChange={set('tempsAvril')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsMai} onChange={set('tempsMai')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsJuin} onChange={set('tempsJuin')} step={0.001} shownDecimals={2} required />
                </div>
                <div className="grid grid-cols-[repeat(7,minmax(100px,1fr))] gap-2 p-1 overflow-auto md:overflow-visible">
                    <div></div>
                    <div className="flex justify-center items-center">{t('Juillet')}</div>
                    <div className="flex justify-center items-center">{t('Août')}</div>
                    <div className="flex justify-center items-center">{t('Septembre')}</div>
                    <div className="flex justify-center items-center">{t('Octobre')}</div>
                    <div className="flex justify-center items-center">{t('Novembre')}</div>
                    <div className="flex justify-center items-center">{t('Décembre')}</div>
                    <div className="flex justify-end items-center text-end pr-5">{t('Durée:')}</div>
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsJuillet} onChange={set('tempsJuillet')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsAout} onChange={set('tempsAout')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsSeptembre} onChange={set('tempsSeptembre')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsOctobre} onChange={set('tempsOctobre')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsNovembre} onChange={set('tempsNovembre')} step={0.001} shownDecimals={2} required />
                    <NumberInput className="w-[100px] min-w-full" value={item.tempsDecembre} onChange={set('tempsDecembre')} step={0.001} shownDecimals={2} required />
                </div>
            </>}
            {(item.frequence === 'A_LA_DEMANDE') && <>
                <Row>
                    <NumberInput label={t('Temps à la demande')} value={item.tempsALaDemande} onChange={set('tempsALaDemande')} step={0.001} shownDecimals={2} required />
                </Row>
            </>}
            {totalTime !== null && <div>
                <div className="flex gap-1">
                    <div className="font-bold">{t("Temps total:")}</div>
                    <div>{t("{temps}h", { temps: totalTime })}</div>
                </div>
                <div className="flex gap-1">
                    <div className="font-bold">{t("Prix horaire:")}</div>
                    <div>{toCurrency(hourlyRate)}</div>
                </div>
            </div>}
        </>}
    </>
}

export function ChantiersList(props) {
    const [achatPopup, setAchatPopup] = useState(false)
    const [ventePopup, setVentePopup] = useState(false)
    const t = useTranslations()
    const typeOptions = useTypeOptions()
    const actions = useActions()
    const today = useToday()

    return <>
        <ListPage model="chantiers" title={t("Chantiers")} columns={{
            [t('Nom')]: 'nom',
            [t("Numéro")]: 'num',
            [t('Code postal')]: 'codePostal',
            [t('Type')]: { sort: 'type', value: item => typeOptions.find(option => option.value === item.type)?.label },
            [t('Prix de vente')]: { sort: 'prixVente', value: item => <Currency value={item.prixVente} /> },
            [t("Date de début")]: 'dateDebut',
            [t("Date de fin")]: 'dateFin',
        }} join={{
            client: 'referenceCourte',
            offre: 'num',
        }} actions={actions} mainActions={{
            [t("Indexer prix d'achat")]: () => setAchatPopup(true),
            [t("Indexer prix de vente")]: () => setVentePopup(true),
            [t("Télécharger fichier de non concurrence")]: { href: `${process.env.REACT_APP_API_URL}/chantiers/non-compete` },
        }} filter={{
            [t('Actifs')]: `dateDebut:lte=${encodeURIComponent(today.toISOString())}&or2:dateFin:gt=${encodeURIComponent(today.toISOString())}&or2:dateFin=`,
            [t('Passés')]: `dateFin:lte=${encodeURIComponent(today.toISOString())}`,
            [t('Futurs')]: `dateDebut:gte=${encodeURIComponent(today.toISOString())}`,
            [t('Tous')]: ``,
        }} {...props} />
        <IndexationPopup value={achatPopup} onChange={setAchatPopup} achat />
        <IndexationPopup value={ventePopup} onChange={setVentePopup} />
    </>
}

export function ChantierDetail(props) {
    const [chantier, setChantier] = useState()
    const [offre, setOffre] = useState()
    const [client, setClient] = useState()
    const t = useTranslations()
    const api = useApi()
    const actions = useActions(client, offre)
    const today = useToday()

    useEffect(() => {
        if (chantier?.offreId) api.get(`/offres/${chantier.offreId}`).then(setOffre)
    }, [chantier?.offreId])
    useEffect(() => {
        if (chantier?.clientId) api.get(`/clients/${chantier.clientId}`).then(setClient)
    }, [chantier?.clientId])

    return <DetailPage model="chantiers" title={t("Chantier")} Fields={ChantierFields} onItemLoad={setChantier} tabs={[t('Administration'), t('Facturation'), t('Heures')]} defaults={{
        indexerPrixAchat: true,
        indexerPrixVente: true,
        majPrixAchat: today,
        majPrixVente: today,
        facturationMensuel: true,
        tempsLundi: 0,
        tempsMardi: 0,
        tempsMercredi: 0,
        tempsJeudi: 0,
        tempsVendredi: 0,
        tempsSamedi: 0,
        tempsDimanche: 0,
        tempsJanvier: 0,
        tempsFevrier: 0,
        tempsMars: 0,
        tempsAvril: 0,
        tempsMai: 0,
        tempsJuin: 0,
        tempsJuillet: 0,
        tempsAout: 0,
        tempsSeptembre: 0,
        tempsOctobre: 0,
        tempsNovembre: 0,
        tempsDecembre: 0,
        tempsALaDemande: 0
    }} actions={actions} tables={{
        [t("Temps")]: <TempsList relation="chantierId" root="." />,
        [t("Prix")]: <PrixChantiersList relation="chantierId" root="." />,
        [t("Codes")]: <CodesList relation="chantierId" root="." />,
        [t("Articles")]: <MaterielList relation="chantierId" chantier root="." />,
        ...(client ? {
            [t("Contacts")]: <ContactsList key={!!client.gerantId} filter={client.gerantId ? {
                [t("Contacts gérant")]: `clientId=&gerantId=${client.gerantId}&clientsActifs=${client.id}`,
                [t("Contacts client")]: `clientId=${client.id}`,
            } : `clientId=${chantier?.clientId}`} title={undefined} root={tab => tab === t("Contacts gérant") ? `/gerants/${client?.gerantId}` : `/clients/${chantier?.clientId}`} />
        } : {}),
        [t("Plaintes client")]: <PlaintesList filter={`clientId=${chantier?.clientId}`} title={undefined} root={`/clients/${chantier?.clientId}`} />,
        [t("Historique client")]: <HistoriquesList filter={`clientId=${chantier?.clientId}`} title={undefined} root={`/clients/${chantier?.clientId}`} />,
        [t("Factures client")]: <FacturesList filter={`clientId=${chantier?.clientId}`} title={undefined} />,
    }} {...props} />
}

function IndexationPopup({ value, onChange, achat }) {
    const societe = useSociete()
    const t = useTranslations()
    const api = useApi()
    const today = useToday()
    const defaultIndexationDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))

    const [indexationPayload, setIndexationPayload, set] = useObjectState({ date: defaultIndexationDate })

    useEffect(() => {
        set('noteFactureFr')(societe?.texteIndexationFr)
        set('noteFactureNl')(societe?.texteIndexationNl)
        set('noteFactureEn')(societe?.texteIndexationEn)
    }, [societe])

    const index = () => {
        onChange(false)
        if (achat) {
            api.post(`/chantiers/index-achat`, {
                ...indexationPayload,
                noteFactureFr: null,
                noteFactureNl: null,
                noteFactureEn: null,
            }).then(() => window.location.reload())
        } else {
            api.post(`/chantiers/index-vente`, indexationPayload).then(() => window.location.reload())
        }
    }
    const cancel = () => {
        onChange(false)
        setIndexationPayload({
            date: defaultIndexationDate,
            noteFactureFr: societe?.texteIndexationFr,
            noteFactureNl: societe?.texteIndexationNl,
            noteFactureEn: societe?.texteIndexationEn,
        })
    }
    const onRateChange = value => {
        set('rate')(value)
        set('memoInterne')(value ? `INDEXATION ${value >= 0 ? '+' : ''}${value}%` : '')
    }

    return <Popup value={value} onChange={onChange} large>
        <Form onSubmit={index} className="flex flex-col gap-3">
            <h2 className="font-bold text-lg">{t("Indexer les chantiers")}</h2>
            <NumberInput label={t("Taux")} value={indexationPayload.rate} onChange={onRateChange} required step="0.01" icon={<FaPercent />} />
            <Row>
                <DateInput className="grow" label={t("Date")} value={indexationPayload.date} onChange={set('date')} required />
                <DateInput className="grow" label={t("Sauf chantiers démarrés après le")} value={indexationPayload.maxStartDate} onChange={set('maxStartDate')} />
            </Row>
            {!achat && <>
                <TextInput label={t("Note sur première facture FR")} value={indexationPayload.noteFactureFr} onChange={set('noteFactureFr')} />
                <TextInput label={t("Note sur première facture NL")} value={indexationPayload.noteFactureNl} onChange={set('noteFactureNl')} />
                <TextInput label={t("Note sur première facture EN")} value={indexationPayload.noteFactureEn} onChange={set('noteFactureEn')} />
            </>}
            <TextareaInput className="[&_textarea]:text-red-600" label={t("Mémo interne")} value={indexationPayload.memoInterne} onChange={set('memoInterne')} required />
            <Row className="mt-5">
                <Button gray icon={<FaBan />} onClick={cancel}>{t('Cancel')}</Button>
                <Button icon={<FaSave />} submit>{t('Save')}</Button>
            </Row>
        </Form>
    </Popup>
} 